import { useCallback } from "react"

import useSWR from "swr"

import { RecentlySearches } from "commonModule/services/common"
import { apiClientCommon } from "services/apiClientCommon"
import { selectIsUserLoggedIn } from "stores/auth/authSlice"
import { useAppSelector } from "stores/hooks"
import {
  addRecentlySearchToStorage,
  clearRecentlySearchesFromStorage,
  getRecentlySearchesFromStorage,
} from "utils/local-storage"

const recentlySearchesFetcher = async (): Promise<RecentlySearches[]> => {
  const response = await apiClientCommon.getRecentlySearches()
  if (!response.data || !response.data.data) {
    return []
  }
  return response.data.data
}

const useRecentlySearches = () => {
  const isLoggedIn = useAppSelector(selectIsUserLoggedIn)

  const { data, error, isLoading, mutate } = useSWR<RecentlySearches[], Error>(
    isLoggedIn ? "/api/recently-searches" : null,
    recentlySearchesFetcher,
    {
      loadingTimeout: 15000,
      revalidateOnFocus: false,
      dedupingInterval: 3000,
      errorRetryCount: 1,
      keepPreviousData: true,
    },
  )
  const saveRecentlySearches = useCallback(
    ({
      saveRecentlySearchedLocationFromLocalStorage = false,
      searchedLocation,
    }: {
      saveRecentlySearchedLocationFromLocalStorage?: boolean
      searchedLocation?: RecentlySearches[]
    }) => {
      setTimeout(() => {
        // Prepare the recently searched locations
        // If saveRecentlySearchedLocationFromLocalStorage is true, get the recently searched locations from localStorage
        // If searchedLocation is provided, use it; otherwise, create a new entry with the current search details
        const recentlySearches = saveRecentlySearchedLocationFromLocalStorage
          ? getRecentlySearchesFromStorage()
          : searchedLocation
        if (
          !(recentlySearches && recentlySearches.length > 0) ||
          !recentlySearches?.[0].name
        )
          return

        // If the user is logged in, save the recently searched locations to the database
        // If the user is not logged in, save the recently searched locations to localStorage
        if (isLoggedIn || saveRecentlySearchedLocationFromLocalStorage) {
          apiClientCommon
            .saveRecentlySearches({
              params: {
                recentSearches: recentlySearches,
              },
            })
            .then(() => {
              // you can mutate the SWR cache here if needed
              void mutate()
              if (saveRecentlySearchedLocationFromLocalStorage) {
                clearRecentlySearchesFromStorage()
              }
              return true
            })
            .catch(() => {
              console.error("Failed to save recently searches")
            })
        } else {
          recentlySearches.forEach((location) => {
            if (!location.name) return
            addRecentlySearchToStorage(location)
          })
        }
      }, 1000) // Delay to ensure the search query is updated
    },
    [isLoggedIn, mutate],
  )

  // If user is not logged in, return data from localStorage
  // if (!isLoggedIn) {
  const localStorageData = getRecentlySearchesFromStorage()
  //   return {
  //     recentlySearches: localStorageData,
  //     isLoading: false,
  //     error: null,
  //   }
  // }

  return {
    recentlySearches: isLoggedIn ? data : localStorageData || [],
    isLoading,
    error,
    mutate,
    saveRecentlySearches,
  }
}

export default useRecentlySearches
